import { tapResponse } from "@ngrx/operators";
import { patchState, WritableStateSource } from "@ngrx/signals";
import { rxMethod, RxMethod } from "@ngrx/signals/rxjs-interop";
import { ConfigService } from "@ph-core/services/config/config.service";
import { Forms } from "@ph-model/form/form";
import { Cart, CartTax } from "@ph-shopping/model/cart";
import { CartItem } from "@ph-shopping/model/cart/cart-item.model";
import { GetProductsRequest } from "@ph-shopping/model/product.request";
import { coverageInfoForm } from "@ph-static-data/coverage-info-form";
import { creditInsuranceForm } from "@ph-static-data/credit-insurance-form";
import { financeForm } from "@ph-static-data/finance-form";
import { UserStoreType } from "@ph-store/user/user.store";
import { of, pipe, switchMap } from "rxjs";
import { CartApiClient } from "../cart-api.client";

export interface CartStoreCheckoutForMethods extends WritableStateSource<object> {
    validatedCart(): CartItem[];
    cart(): CartItem[];
    checkoutForms(): Forms;
}

export interface CartStoreCheckoutDeps {
    userStore: UserStoreType;
    cartApiClient: CartApiClient;
}

export interface CartStoreCheckoutMethodsReturn {
    getCheckoutForm: RxMethod<{ productsRequest: GetProductsRequest }>;
    addTaxes: RxMethod<{ productsRequest: GetProductsRequest }>;
}

export function withCheckoutMethods(
    store: CartStoreCheckoutForMethods,
    deps: CartStoreCheckoutDeps
): CartStoreCheckoutMethodsReturn {

    const environment = ConfigService.getEnvironment();

    const getCheckoutForm = rxMethod<{ productsRequest: GetProductsRequest }>(
        pipe(
            switchMap(({ productsRequest }) => {
                const { userStore, cartApiClient } = deps;

                const user = userStore.user();
                if (!user) {
                    console.error('User not found. Getting checkout form aborted.');
                    return of([]);
                }
                const req = new Cart(store.cart(), productsRequest, user);
                return cartApiClient
                    .getCheckoutForm({
                        ...req,
                        application: environment.application!,
                        programId: user.programId,
                    })
                    .pipe(
                        tapResponse<Forms>({
                            next: (forms: Forms) => {
                                forms.creditInsuranceForm = creditInsuranceForm;
                                forms.coverageInfoForm = coverageInfoForm;
                                forms.financeForm = financeForm;

                                patchState(store, {
                                    checkoutForms: { ...store.checkoutForms(), ...forms },
                                    submittedCartSuccess: false,
                                });
                            },
                            error: (error: unknown) => {
                                const errorMessage =
                                    (error && typeof error === 'object' && 'error' in error && typeof error.error === 'string'
                                        ? error.error
                                        : String(error)) || '';
                                console.error(errorMessage);

                                patchState(store, {
                                    error: errorMessage,
                                    success: false,
                                    savedQuote: false,
                                });
                            },
                        })
                    );
            })
        )
    );

    const addTaxes = rxMethod<{ productsRequest: GetProductsRequest }>(
        pipe(
            switchMap(({ productsRequest }) => {
                const { userStore, cartApiClient } = deps;
                const user = userStore.user();
                if (!user) {
                    console.error('User not found. Tax calculation aborted.');
                    return of([]);
                }
                const request = new CartTax(user, store.validatedCart(), productsRequest);

                return cartApiClient.taxCalculation(request).pipe(
                    tapResponse<{ cart: CartItem[] }>({
                        next: ({ cart }: { cart: CartItem[] }) => {
                            patchState(store, { validatedCart: cart });
                        },
                        error: (error: unknown) => {
                            const errorMessage =
                                (error && typeof error === 'object' && 'error' in error && typeof error.error === 'string'
                                    ? error.error
                                    : String(error)) || '';
                            console.error(errorMessage);

                            patchState(store, {
                                error: errorMessage,
                                success: false,
                                savedQuote: false,
                            });
                        },
                    })
                );
            })
        )
    );

    return {
        getCheckoutForm,
        addTaxes
    }
}